import { Directive, ElementRef, Input, OnDestroy, afterNextRender, inject } from '@angular/core';
import { gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

export interface ParallaxConfig {
  /** yPercent from/to. */
  y?: number[];
  /** xPercent from/to. */
  x?: number[];
  /** scale from/to. */
  scale?: number[];
  trigger?: 'section' | 'parent' | 'self';
  start?: string;
  end?: string;
  /** Multiplier for the travel on mobile. */
  mobileFactor?: number;
}

const DEFAULTS: Required<Omit<ParallaxConfig, 'x' | 'scale'>> = {
  y: [-12, 12],
  trigger: 'section',
  start: 'top bottom',
  end: 'bottom top',
  mobileFactor: 0.6,
};

/** Scroll-scrubbed parallax. Under reduced motion the element simply stays where CSS puts it. */
@Directive({ selector: '[appParallax]' })
export class ParallaxDirective implements OnDestroy {
  @Input('appParallax') config: ParallaxConfig | '' = '';

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  constructor() {
    this.el.classList.add('dv-parallax');
    afterNextRender(() => this.motion.whenNeeded(this.el, () => this.destroyed || this.setup()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  private setup(): void {
    const el = this.el;
    const cfg = { ...DEFAULTS, ...(this.config || {}) };
    const mm = (this.mm = gsap.matchMedia(el));
    mm.add({ motion: QUERIES.motion, mobile: QUERIES.mobile }, (ctx) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion']) return;
      const f = conditions['mobile'] ? cfg.mobileFactor : 1;

      const from: gsap.TweenVars = {};
      const to: gsap.TweenVars = {};
      if (cfg.y) [from['yPercent'], to['yPercent']] = [cfg.y[0] * f, cfg.y[1] * f];
      if (cfg.x) [from['xPercent'], to['xPercent']] = [cfg.x[0] * f, cfg.x[1] * f];
      if (cfg.scale) [from['scale'], to['scale']] = [1 + (cfg.scale[0] - 1) * f, 1 + (cfg.scale[1] - 1) * f];

      const trigger =
        cfg.trigger === 'self' ? el : cfg.trigger === 'parent' ? el.parentElement ?? el : el.closest('section') ?? el.parentElement ?? el;

      gsap.fromTo(el, from, {
        ...to,
        ease: 'none',
        scrollTrigger: { trigger, start: cfg.start, end: cfg.end, scrub: true, invalidateOnRefresh: true },
      });
    });
  }
}
